import React from "react";
import { useTranslation } from "react-i18next";
import Icon from "../../components/AppIcon";
import { cn } from "../../utils/cn";
import ModuleBrochure, {
  type BrochureFeature,
  type BrochureMetric,
  type BrochureUseCase,
} from "./ModuleBrochure";

/**
 * Development brochure — Phase 6 build.
 *
 * Tone: "growth is a weekly habit, not a Q1 form". Pitch is calibrated to
 * the engineering manager preparing promotion packets and the employee who
 * wants their next role to be more than a wish list.
 *
 * Illustration: a synthetic "journey" SVG — three milestones along a curved
 * path, each capped with a small skill chip; a progress trail fills the
 * first half. One floating accent pill labelled "+3 skills this quarter"
 * sits on the trailing edge. No external assets.
 *
 * Mockup: a faux "My Development" page — two active objectives with
 * progress bars and due dates on the left; a mentor card and the next 1:1
 * callout on the right. Tokens mirror the real app's list patterns.
 */

// ----- Illustration: journey path with milestones + accent pill -----

const DevelopmentIllustration: React.FC = () => {
  const { t, i18n } = useTranslation();
  const isRtl = i18n.dir() === "rtl";

  return (
    <div className="relative aspect-[4/5] w-full">
      {/* Path + milestones rendered as inline SVG so they scale cleanly. */}
      <svg
        viewBox="0 0 320 400"
        className="absolute inset-0 h-full w-full"
        aria-hidden="true"
      >
        <defs>
          <pattern id="dev-grid" width="24" height="24" patternUnits="userSpaceOnUse">
            <path
              d="M 24 0 L 0 0 0 24"
              fill="none"
              stroke="rgb(16 185 129 / 0.08)"
              strokeWidth="1"
            />
          </pattern>
          <linearGradient id="dev-progress" x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stopColor="rgb(16 185 129 / 0.9)" />
            <stop offset="100%" stopColor="rgb(5 150 105 / 0.9)" />
          </linearGradient>
        </defs>
        <rect width="320" height="400" fill="url(#dev-grid)" />

        {/* Background path — full route from milestone 1 → 3. */}
        <path
          d="M 50 320 C 110 280, 90 200, 160 180 S 230 110, 270 80"
          fill="none"
          stroke="rgb(148 163 184 / 0.45)"
          strokeWidth="3"
          strokeLinecap="round"
          strokeDasharray="6 6"
        />
        {/* Progress trail — fills the first half of the path. */}
        <path
          d="M 50 320 C 110 280, 90 200, 160 180"
          fill="none"
          stroke="url(#dev-progress)"
          strokeWidth="4"
          strokeLinecap="round"
        />
      </svg>

      {/* Milestone 1 — completed, near the start (bottom-left). */}
      <div className="absolute bottom-[12%] start-[10%] flex flex-col items-center gap-1.5">
        <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-emerald-600 text-white shadow-[0_10px_24px_-8px_rgba(5,150,105,0.55)]">
          <Icon name="BookOpen" size={20} aria-hidden="true" />
        </span>
        <span className="rounded-full bg-white px-2 py-0.5 text-[10px] font-semibold text-emerald-800 shadow-[0_4px_12px_-4px_rgba(15,23,42,0.25)] ring-1 ring-emerald-200">
          {t("brochure.development.illustration.milestone.foundations")}
        </span>
      </div>

      {/* Milestone 2 — current, mid-journey. */}
      <div className="absolute top-[42%] start-1/2 -translate-x-1/2 flex flex-col items-center gap-1.5">
        <span className="flex h-14 w-14 items-center justify-center rounded-2xl bg-emerald-500 text-white shadow-[0_12px_28px_-8px_rgba(16,185,129,0.6)] ring-4 ring-emerald-100">
          <Icon name="Target" size={22} aria-hidden="true" />
        </span>
        <span className="rounded-full bg-emerald-600 px-2.5 py-0.5 text-[10px] font-semibold text-white shadow-[0_4px_12px_-4px_rgba(15,23,42,0.35)]">
          {t("brochure.development.illustration.milestone.current")}
        </span>
      </div>

      {/* Milestone 3 — upcoming, near the top-end. */}
      <div className="absolute top-[10%] end-[8%] flex flex-col items-center gap-1.5">
        <span className="flex h-12 w-12 items-center justify-center rounded-2xl border-2 border-dashed border-emerald-300 bg-white text-emerald-600">
          <Icon name="Award" size={20} aria-hidden="true" />
        </span>
        <span className="rounded-full bg-white px-2 py-0.5 text-[10px] font-semibold text-slate-500 shadow-[0_4px_12px_-4px_rgba(15,23,42,0.2)] ring-1 ring-slate-200">
          {t("brochure.development.illustration.milestone.next")}
        </span>
      </div>

      {/* Floating accent pill — "+3 skills this quarter". */}
      <div
        className={cn(
          "absolute top-[6%] flex items-center gap-1.5 rounded-full bg-emerald-600 px-3 py-1.5",
          "text-[11px] font-semibold text-white shadow-[0_10px_24px_-8px_rgba(5,150,105,0.55)]",
          isRtl ? "right-[6%]" : "left-[6%]",
        )}
      >
        <Icon name="Sparkles" size={12} aria-hidden="true" />
        <span>{t("brochure.development.illustration.skillsPill")}</span>
      </div>
    </div>
  );
};

// ----- Mockup: "My Development" page -----

interface ObjectiveRow {
  titleKey: string;
  dueKey: string;
  progress: number;
  icon: "Target" | "BookOpen" | "Sparkles";
  tint: string;
}

const OBJECTIVES: ObjectiveRow[] = [
  {
    titleKey: "brochure.development.mockup.objective.systemDesign.title",
    dueKey: "brochure.development.mockup.objective.systemDesign.due",
    progress: 70,
    icon: "Target",
    tint: "bg-emerald-50 text-emerald-700",
  },
  {
    titleKey: "brochure.development.mockup.objective.mentorship.title",
    dueKey: "brochure.development.mockup.objective.mentorship.due",
    progress: 35,
    icon: "BookOpen",
    tint: "bg-teal-50 text-teal-700",
  },
];

const DevelopmentMockup: React.FC = () => {
  const { t } = useTranslation();
  return (
    <div className="grid gap-4 lg:grid-cols-[1fr_220px]">
      {/* Objectives list */}
      <div className="overflow-hidden rounded-lg border border-slate-200">
        <div className="flex items-center justify-between border-b border-slate-200 bg-slate-50 px-4 py-2.5">
          <div className="flex items-center gap-2">
            <Icon name="Target" size={14} className="text-slate-500" aria-hidden="true" />
            <p className="text-xs font-semibold uppercase tracking-wider text-slate-600">
              {t("brochure.development.mockup.heading")}
            </p>
          </div>
          <p className="text-[10px] font-medium text-slate-400">
            {t("brochure.development.mockup.cycle")}
          </p>
        </div>

        <ul className="divide-y divide-slate-100 bg-white">
          {OBJECTIVES.map((o, i) => (
            <li key={i} className="px-4 py-3.5">
              <div className="flex items-center gap-3">
                <span
                  className={cn(
                    "inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-lg",
                    o.tint,
                  )}
                >
                  <Icon name={o.icon} size={16} aria-hidden="true" />
                </span>
                <div className="min-w-0 flex-1">
                  <p className="text-xs font-semibold text-slate-900">{t(o.titleKey)}</p>
                  <p className="text-[10px] text-slate-500 tabular-nums">{t(o.dueKey)}</p>
                </div>
                <span className="text-xs font-semibold tabular-nums text-slate-900">
                  {o.progress}%
                </span>
              </div>
              {/* Progress bar */}
              <div className="mt-2.5 h-1.5 w-full overflow-hidden rounded-full bg-slate-100">
                <div
                  className="h-full rounded-full bg-emerald-500"
                  style={{ width: `${o.progress}%` }}
                  aria-hidden="true"
                />
              </div>
            </li>
          ))}
        </ul>
      </div>

      {/* Sidebar: mentor card + next 1:1 callout */}
      <div className="grid gap-3">
        <div className="rounded-lg border border-slate-200 bg-white p-4">
          <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">
            {t("brochure.development.mockup.mentor.heading")}
          </p>
          <div className="mt-2 flex items-center gap-2.5">
            <span className="inline-flex h-9 w-9 items-center justify-center rounded-full bg-emerald-100 text-[11px] font-semibold text-emerald-800">
              AS
            </span>
            <div className="leading-tight">
              <p className="text-xs font-semibold text-slate-900">
                {t("brochure.development.mockup.mentor.name")}
              </p>
              <p className="text-[10px] text-slate-500">
                {t("brochure.development.mockup.mentor.role")}
              </p>
            </div>
          </div>
          <p className="mt-2 text-[10px] leading-snug text-slate-500">
            {t("brochure.development.mockup.mentor.hint")}
          </p>
        </div>
        <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-4">
          <p className="text-[10px] font-semibold uppercase tracking-wider text-emerald-800">
            {t("brochure.development.mockup.next11.heading")}
          </p>
          <p className="mt-1.5 text-sm font-semibold text-emerald-900">
            {t("brochure.development.mockup.next11.when")}
          </p>
          <p className="mt-1 text-[10px] leading-snug text-emerald-900/80">
            {t("brochure.development.mockup.next11.hint")}
          </p>
        </div>
      </div>
    </div>
  );
};

// ----- Brochure props ------

const DevelopmentBrochure: React.FC = () => {
  const { t } = useTranslation();

  const features: BrochureFeature[] = [
    {
      icon: "Target",
      title: t("brochure.development.features.objectives.title"),
      description: t("brochure.development.features.objectives.description"),
    },
    {
      icon: "Users",
      title: t("brochure.development.features.mentorship.title"),
      description: t("brochure.development.features.mentorship.description"),
    },
    {
      icon: "Sparkles",
      title: t("brochure.development.features.skills.title"),
      description: t("brochure.development.features.skills.description"),
    },
    {
      icon: "BarChart3",
      title: t("brochure.development.features.teamDashboard.title"),
      description: t("brochure.development.features.teamDashboard.description"),
    },
    {
      icon: "Layers",
      title: t("brochure.development.features.frameworks.title"),
      description: t("brochure.development.features.frameworks.description"),
    },
  ];

  const metrics: BrochureMetric[] = [
    {
      value: t("brochure.development.metrics.oneOnOneValue"),
      label: t("brochure.development.metrics.oneOnOneLabel"),
    },
    {
      value: "76%",
      label: t("brochure.development.metrics.completionLabel"),
    },
    {
      value: t("brochure.development.metrics.benchValue"),
      label: t("brochure.development.metrics.benchLabel"),
    },
  ];

  const useCases: BrochureUseCase[] = [
    {
      persona: t("brochure.development.useCases.manager.persona"),
      scenario: t("brochure.development.useCases.manager.scenario"),
      outcome: t("brochure.development.useCases.manager.outcome"),
    },
    {
      persona: t("brochure.development.useCases.employee.persona"),
      scenario: t("brochure.development.useCases.employee.scenario"),
      outcome: t("brochure.development.useCases.employee.outcome"),
    },
  ];

  return (
    <ModuleBrochure
      slug="development"
      hero={{
        eyebrow: t("brochure.development.hero.eyebrow"),
        headline: t("brochure.development.hero.headline"),
        subheadline: t("brochure.development.hero.subheadline"),
        illustration: <DevelopmentIllustration />,
      }}
      features={features}
      metrics={metrics}
      mockup={{
        title: t("brochure.development.mockup.caption"),
        content: <DevelopmentMockup />,
      }}
      useCases={useCases}
    />
  );
};

export default DevelopmentBrochure;
